/*
 * Instant sync setup (2026-11-03), super-admin only. Notion's webhook makes a
 * ticket change sync within about a minute instead of waiting for the 40-min
 * scan. Setting it up: create a subscription on the launcher's Notion
 * connection pointing at the URL below, then paste the token shown here into
 * Notion's Verify box. The panel then says when the last event arrived.
 */

import { Button, Code, CopyButton, Group, Stack, Text } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getNotionWebhook, resetNotionWebhook } from "../../api/endpoints";
import { errorMessage } from "../../api/errors";
import { formatDateTime } from "../../lib/format";

export function WebhookPanel() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["notion-webhook"], queryFn: getNotionWebhook, refetchInterval: 30_000 });
  const reset = useMutation({
    mutationFn: resetNotionWebhook,
    onSuccess: (data) => qc.setQueryData(["notion-webhook"], data),
  });

  if (status.isLoading) return null;
  if (status.isError) {
    return <Text fz="xs" c="red">Instant sync status could not be read: {errorMessage(status.error)}</Text>;
  }
  const s = status.data!;
  const url = `${window.location.origin}${s.path}`;

  let state: string;
  if (!s.verification_token) state = "Not set up. Events from Notion are not arriving, so tickets wait for the 40-minute scan.";
  else if (!s.confirmed_at) state = "Waiting for Notion. Paste the token below into the subscription's Verify box.";
  else state = s.last_event_at ? `On. Last event ${formatDateTime(s.last_event_at)} (${s.last_event_type ?? "unknown"}).` : "On. No event yet.";

  return (
    <Stack gap={6} data-testid="webhook-panel">
      <Text fz="sm">
        <strong>Instant sync (Notion webhook):</strong> {state}
      </Text>
      <Group gap="xs">
        <Text fz="xs" c="dimmed">Webhook URL:</Text>
        <Code>{url}</Code>
        <CopyButton value={url}>
          {({ copied, copy }) => (
            <Button variant="default" size="compact-xs" onClick={copy}>{copied ? "Copied" : "Copy"}</Button>
          )}
        </CopyButton>
      </Group>
      {s.verification_token && !s.confirmed_at && (
        <Group gap="xs">
          <Text fz="xs" c="dimmed">Verification token:</Text>
          <Code>{s.verification_token}</Code>
          <CopyButton value={s.verification_token}>
            {({ copied, copy }) => (
              <Button variant="default" size="compact-xs" onClick={copy}>{copied ? "Copied" : "Copy"}</Button>
            )}
          </CopyButton>
        </Group>
      )}
      {s.verification_token && (
        <Group gap="xs">
          <Button
            variant="default"
            size="compact-xs"
            loading={reset.isPending}
            onClick={() => {
              if (window.confirm("Forget the Notion token? Instant sync stops until Notion is verified again.")) reset.mutate();
            }}
          >
            Reset token
          </Button>
          {reset.isError && <Text fz="xs" c="red">{errorMessage(reset.error)}</Text>}
        </Group>
      )}
    </Stack>
  );
}
